"use client"

import { useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"

export default function ProductQA() {
  const [showForm, setShowForm] = useState(false)
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [question, setQuestion] = useState("")
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const closeForm = () => {
    setShowForm(false)
    setError(null)
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!recaptchaToken) {
      setError("Please verify you are not a robot")
      return
    }

    setSubmitted(true)
    setShowForm(false)
    setError(null)
    setName("")
    setEmail("")
    setQuestion("")
    setRecaptchaToken(null)
    recaptchaRef.current?.reset()
  }

  return (
    <div data-testid="product-qa">
      <div className="flex items-center justify-between gap-3 mb-2.5">
        <h3 className="text-base font-medium text-gray-900">
          Questions and answers about this product
        </h3>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="flex-shrink-0 px-3 py-1.5 rounded text-[13px] text-white bg-[#007bc4] hover:bg-[#0069a8] transition-colors"
          data-testid="ask-question-button"
        >
          Ask a Question
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="border-t border-gray-200 mt-2 pt-4 space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Name (will be displayed on site)
              <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full h-11 px-3 border border-brand-navy rounded-[3px] text-sm text-gray-900 outline-none focus:ring-2 focus:ring-brand-navy"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Email (will NOT be displayed on site)
              <span className="text-red-600">*</span>
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-11 px-3 border border-brand-navy rounded-[3px] text-sm text-gray-900 outline-none focus:ring-2 focus:ring-brand-navy"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Question
              <span className="text-red-600">*</span>
            </label>
            <textarea
              required
              rows={5}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              className="w-full px-3 py-2 border border-brand-navy rounded-[3px] text-sm text-gray-900 outline-none focus:ring-2 focus:ring-brand-navy"
            />
          </div>

          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string}
            onChange={(token) => {
              setRecaptchaToken(token)
              setError(null)
            }}
            onExpired={() => setRecaptchaToken(null)}
          />
          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex gap-3">
            <button
              type="submit"
              className="px-5 py-2.5 rounded text-sm text-white bg-[#007bc4] hover:bg-[#0069a8] transition-colors"
            >
              Submit
            </button>
            <button
              type="button"
              onClick={closeForm}
              className="px-5 py-2.5 rounded text-sm text-gray-600 border border-gray-400 hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </form>
      )}

      {submitted && (
        <p className="text-sm text-green-700 mt-3">
          Thanks — your question has been received.
        </p>
      )}

      <div className="border-t border-gray-200 mt-4 pt-4">
        <p className="text-sm font-bold text-gray-900">
          No Question/Answer available this time.
        </p>
      </div>
    </div>
  )
}
